import {BehaviorSubject} from 'rxjs';
import {IProduct, IOrder, IOrderSummary} from './geoshop_api';
import Geometry from 'ol/geom/Geometry';
import GeoJSON from 'ol/format/GeoJSON';
import mapModel from 'gmfapi/store/map';

import Collection from 'ol/Collection';
import GeometryCollection from 'ol/geom/GeometryCollection';
import Feature from 'ol/Feature';
import Style from 'ol/style/Style';
import ngeoMapFeatureOverlayMgr from 'ngeo/map/FeatureOverlayMgr';

import geoshop from './geoshop';

export class CartEmptyError extends Error {}
export class NoExtentError extends Error {}

export class GeoshopOrder {
  orderProducts: BehaviorSubject<IProduct[]> = new BehaviorSubject<IProduct[]>([]);
  currentOrder: BehaviorSubject<IOrder | null> = new BehaviorSubject<IOrder | null>(null);
  extent: BehaviorSubject<Geometry | null> = new BehaviorSubject<Geometry | null>(null);
  features: Collection<Feature> = new Collection();
  private orderCheckInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    const cart = localStorage.getItem('geoshop_cart');
    if (cart) {
      this.orderProducts.next(JSON.parse(cart));
    }
    const extent_json = localStorage.getItem('geoshop_extent');
    if (extent_json) {
      const geometry = new GeoJSON().readGeometry(extent_json);
      this.setExtentOverlay(geometry);
      this.hideOverlay();
      this.extent.next(geometry);
    }
    const overlay = ngeoMapFeatureOverlayMgr.getFeatureOverlay();
    overlay.setFeatures(this.features);
  }

  setCart(products: IProduct[]) {
    this.orderProducts.next(products);
    const json = JSON.stringify(products);
    localStorage.setItem('geoshop_cart', json);
  }

  addProduct(product: IProduct) {
    this.setCart([...this.orderProducts.value, product]);
  }
  removeProduct(id: number) {
    const index = this.orderProducts.value.findIndex((i) => i.id === id);
    const new_products = [...this.orderProducts.value];
    new_products.splice(index, 1);
    this.setCart(new_products);
  }
  setExtent(geometry: Geometry | null) {
    this.extent.next(geometry);
    this.setExtentOverlay(geometry);
    if (geometry === null) {
      localStorage.removeItem('geoshop_extent');
    } else {
      const json: string = new GeoJSON().writeGeometry(geometry);
      localStorage.setItem('geoshop_extent', json);
    }
  }

  setExtentOverlay(geometry: Geometry | null) {
    this.features.clear();
    if (geometry !== null) {
      this.features.push(new Feature({geometry}));
    }
  }

  hideOverlay() {
    this.features.forEach((f) => f.setStyle(new Style({image: undefined})));
  }

  showOverlay() {
    if (this.features.getLength()) {
      this.features.forEach((f) => f.setStyle(undefined));
      this.zoomToExtent();
    }
  }

  zoomToExtent() {
    const coll = new GeometryCollection(this.features.getArray().map((f) => f.getGeometry()!));
    const extent = coll.getExtent();

    // timeout needed to allow the panel to show, so that the viewport size is the one
    // after the map has been shrunk by the tool panel
    setTimeout(
      () => mapModel.getMap().subscribe((map) => map.getView().fit(extent, {padding: [50, 50, 50, 50]})),
      100,
    );
  }

  async makeOrder(isPrivate: boolean) {
    if (this.extent.value === null) {
      throw new NoExtentError('No extent');
    }
    if (this.orderProducts.value.length === 0) {
      throw new CartEmptyError('Cart Empty');
    }
    const order = await geoshop.sendOrder(this.orderProducts.value, this.extent.value, isPrivate);
    this.confirmOrder(order);
    this.setCart([]);
    this.clearextent();
    this.currentOrder.next(order);
  }

  async confirmOrder(order: {id: number; items: {id: number; available_formats: string[]}[]}) {
    // choose first data format for each item
    let response;
    order.items.forEach(async (item) => {
      response = await geoshop.fetchWithApiBase(`orderitem/${item.id}/`, {
        method: 'PATCH',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify({data_format: item.available_formats[0]}),
      });
    });
    const orderToValidate = await geoshop.getOrder(order.id);
    response = await geoshop.fetchWithApiBase(`validate/order`, {
      method: 'POST',
      headers: {'content-type': 'application/json'},
      body: JSON.stringify(orderToValidate),
    });
    if (!response.ok) {
      console.log(`error validating order no ${order.id} - not confirmed`);
      return;
    }
    response = await geoshop.fetchWithApiBase(`order/${order.id}/confirm/`);
    if (!response.ok) {
      // (response.status_code !== 202) {
      console.log(`error confirming order no ${order.id} - not confirmed`);
    }
    this.monitorOrder(order.id);
  }

  monitorOrder(id: number) {
    if (this.orderCheckInterval) {
      clearInterval(this.orderCheckInterval!);
    }
    this.orderCheckInterval = setInterval(async () => {
      const response = await geoshop.fetchWithApiBase(`order/?search=${id}`);
      const orderJson = await response.json();
      this.currentOrder.next(orderJson.results[0]);
      if (orderJson.results[0].order_status === 'PROCESSED') {
        clearInterval(this.orderCheckInterval!);
      }
    }, 5000);
  }

  async downloadOrder(order: IOrderSummary) {
    const response = await geoshop.fetchWithApiBase(`download/${order.download_guid}/result`);
    const blob = await response.blob();
    const link = document.createElement('a');
    link.download = `${order.title.replace(/[^a-zA-Z0-9]/g, '_')}.zip`;
    link.href = window.URL.createObjectURL(blob);
    link.click();
    window.URL.revokeObjectURL(link.href);
  }
}

const geoshopOrder = new GeoshopOrder();
export default geoshopOrder;
